import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowLeft, Save, User, Mail, Lock, UserCog, Building,
  Handshake, AlertCircle, X, Check, Users, Plus, Trash2, CreditCard
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

export default function NewUser() {
  // Estados del formulario
  const [formData, setFormData] = useState({
    nombrec: '',
    username: '',
    email: '',
    password: '',
    permisos: '5', // Cliente por defecto
    clinica_matriz: '', // Clínica matriz principal
    proveedor_asociado: '',
    importe_min: '', // Límite de gasto por usuario (opcional)
    importe_max: '', // Límite de gasto por usuario (opcional)
    aprobador_id: '' // Aprobador específico (opcional): vacío = flujo matriz
  });

  // Nuevo estado para clínicas matriz adicionales
  const [clinicasAdicionales, setClinicasAdicionales] = useState([]);
  const [nuevaClinicaAdicional, setNuevaClinicaAdicional] = useState('');

  // Estados para autocompletado
  const [clients, setClients] = useState([]);
  const [providers, setProviders] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [filteredAdditionalClients, setFilteredAdditionalClients] = useState([]);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [showProviderSuggestions, setShowProviderSuggestions] = useState(false);
  const [showAdditionalClientSuggestions, setShowAdditionalClientSuggestions] = useState(false);

  // Candidatos a aprobador (usuarios de la red de la clínica seleccionada + admins)
  const [aprobadores, setAprobadores] = useState([]);

  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [errors, setErrors] = useState({});

  // Cargar datos iniciales
  useEffect(() => {
    loadClients();
    loadProviders();
  }, []);

  // Cargar candidatos a aprobador según la clínica principal elegida. Si no hay
  // clínica resuelta, el endpoint devuelve solo admins. Si el aprobador ya elegido
  // dejó de ser válido para la nueva lista, se limpia.
  useEffect(() => {
    const token = localStorage.getItem('token');
    const cli = clients.find((c) => c.razon_social === formData.clinica_matriz);
    const clinicaId = cli ? cli.id : 0;
    const qs = clinicaId ? `?clinica_id=${clinicaId}` : '';
    fetch(`${API_URL}/api/usuarios/aprobadores${qs}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : { aprobadores: [] }))
      .then((d) => {
        const list = d.aprobadores || [];
        setAprobadores(list);
        setFormData((prev) =>
          prev.aprobador_id && !list.some((a) => String(a.id) === String(prev.aprobador_id))
            ? { ...prev, aprobador_id: '' }
            : prev
        );
      })
      .catch(() => setAprobadores([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.clinica_matriz, clients]);

  // Cargar clientes
  const loadClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clientes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClients(data.clientes || []);
      }
    } catch (error) {
      console.error('Error al cargar clientes:', error);
    }
  };

  // Busca clientes en el SERVIDOR por nombre/CIF (no solo en el lote precargado),
  // igual que Gestión de Clientes. Fusiona los resultados en `clients` para que
  // la resolución de id por razon_social al guardar siga funcionando.
  const buscarClientes = async (value) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clientes?search=${encodeURIComponent(value)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const results = data.clientes || [];
        setClients(prev => {
          const map = new Map(prev.map(c => [c.id, c]));
          results.forEach(c => map.set(c.id, c));
          return Array.from(map.values());
        });
        return results;
      }
    } catch (error) {
      console.error('Error al buscar clientes:', error);
    }
    return [];
  };

  // Debounce: no lanzar una petición por cada tecla; espera ~300ms tras dejar
  // de escribir y hace una sola búsqueda.
  const searchDebounceRef = useRef(null);
  const buscarClientesDebounced = (value, callback) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      const results = await buscarClientes(value);
      callback(results);
    }, 300);
  };

  // Cargar proveedores
  const loadProviders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/proveedores`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProviders(data.proveedores || []);
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    }
  };

  // Función para agregar clínica adicional
  const agregarClinicaAdicional = () => {
    if (!nuevaClinicaAdicional.trim()) {
      showMessage('Selecciona una clínica para agregar', 'warning');
      return;
    }

    const client = clients.find(c => c.razon_social === nuevaClinicaAdicional);
    if (!client) {
      showMessage('Selecciona una clínica válida de la lista', 'warning');
      return;
    }

    // Verificar que no sea la clínica principal
    const clinicaPrincipal = clients.find(c => c.razon_social === formData.clinica_matriz);
    if (clinicaPrincipal && client.id === clinicaPrincipal.id) {
      showMessage('Esta clínica ya está asignada como clínica principal', 'warning');
      return;
    }

    // Verificar que no esté ya en la lista
    if (clinicasAdicionales.some(c => c.id === client.id)) {
      showMessage('Esta clínica ya está en la lista de clínicas adicionales', 'warning');
      return;
    }

    setClinicasAdicionales(prev => [...prev, client]);
    setNuevaClinicaAdicional('');
    setShowAdditionalClientSuggestions(false);
  };

  // Función para eliminar clínica adicional
  const eliminarClinicaAdicional = (clienteId) => {
    setClinicasAdicionales(prev => prev.filter(c => c.id !== clienteId));
  };

  // Manejar cambios en el formulario
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Autocompletado para clientes (clínica principal)
    if (field === 'clinica_matriz') {
      if (value.length > 0) {
        setShowClientSuggestions(true);
        buscarClientesDebounced(value, results => setFilteredClients(results));
      } else {
        setShowClientSuggestions(false);
      }
    }

    // Autocompletado para proveedores
    if (field === 'proveedor_asociado') {
      if (value.length > 0) {
        const filtered = providers.filter(provider => 
          provider.nombre.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredProviders(filtered);
        setShowProviderSuggestions(true);
      } else {
        setShowProviderSuggestions(false);
      }
    }
  };

  // Manejar cambios en clínicas adicionales
  const handleAdditionalClientChange = (value) => {
    setNuevaClinicaAdicional(value);
    
    if (value.length > 0) {
      setShowAdditionalClientSuggestions(true);
      buscarClientesDebounced(value, results => {
        const filtered = results.filter(client =>
          // Excluir clínica principal si está seleccionada
          (!formData.clinica_matriz || client.razon_social !== formData.clinica_matriz) &&
          // Excluir clínicas ya agregadas
          !clinicasAdicionales.some(c => c.id === client.id)
        );
        setFilteredAdditionalClients(filtered);
      });
    } else {
      setShowAdditionalClientSuggestions(false);
    }
  };

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nombrec.trim()) newErrors.nombrec = 'El nombre es requerido';
    if (!formData.username.trim()) newErrors.username = 'El usuario es requerido';
    if (!formData.email.trim()) newErrors.email = 'El email es requerido';
    if (!formData.password.trim()) newErrors.password = 'La contraseña es requerida';
    
    // Validar email
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    // Validar contraseña
    if (formData.password && formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Enviar formulario
  const handleSubmit = async () => {
    if (!validateForm()) {
      showMessage('Por favor corrige los errores en el formulario', 'danger');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      // Buscar ID de cliente principal
      let clienteId = null;
      if (formData.clinica_matriz) {
        const client = clients.find(c => c.razon_social === formData.clinica_matriz);
        clienteId = client ? client.id : null;
      }

      // Buscar ID de proveedor
      let proveedorId = null;
      if (formData.proveedor_asociado) {
        const provider = providers.find(p => p.nombre === formData.proveedor_asociado);
        proveedorId = provider ? provider.id : null;
      }

      const userData = {
        username: formData.username,
        password: formData.password,
        nombrec: formData.nombrec,
        email: formData.email,
        permisos: parseInt(formData.permisos),
        clinica_matriz: clienteId || 0,
        proveedor_asociado: proveedorId || 0,
        // Agregar clínicas adicionales
        clinicas_adicionales: clinicasAdicionales.map(c => c.id),
        // Límite de gasto de este usuario (opcional). Vacío → null (el backend
        // normaliza). Techo = importe_max; dispara aprobación si un pedido lo supera.
        importe_min: formData.importe_min,
        importe_max: formData.importe_max,
        // Aprobador específico (opcional). Vacío → null → flujo de matriz de siempre.
        aprobador_id: formData.aprobador_id || null
      };

      console.log('Datos del usuario a enviar:', userData);
      console.log('Clínicas adicionales seleccionadas:', clinicasAdicionales);

      const response = await fetch(`${API_URL}/api/usuarios`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al crear usuario');
      }

      showMessage('Usuario creado correctamente', 'success');
      
      // Limpiar formulario
      setFormData({
        nombrec: '',
        username: '',
        email: '',
        password: '',
        permisos: '5',
        clinica_matriz: '',
        proveedor_asociado: '',
        importe_min: '',
        importe_max: '',
        aprobador_id: ''
      });
      setClinicasAdicionales([]);
      setNuevaClinicaAdicional('');

      // Redirigir después de un momento - SOLUCIÓN AL PROBLEMA DE REDIRECCIÓN
      setTimeout(() => {
        // Usar replace en lugar de href para evitar problemas de router
        window.location.replace('/usuarios');
        // Alternativa si usas React Router:
        // navigate('/usuarios');
      }, 2000);

    } catch (error) {
      console.error('Error al crear usuario:', error);
      showMessage(error.message || 'Error al crear usuario', 'danger');
    } finally {
      setSaving(false);
    }
  };

  // Función para mostrar mensajes
  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  // Función para manejar "Volver"
  const handleGoBack = () => {
    // Usar replace también aquí para consistencia
    window.location.replace('/usuarios');
    // Alternativa si usas React Router:
    // navigate('/usuarios');
  };

  // Función para obtener etiquetas de tipos de usuario
  const getUserTypeLabels = () => {
    return [
      { value: '1', label: 'Super Administrador', description: 'Acceso total al sistema' },
      { value: '2', label: 'Administrador', description: 'Gestión de usuarios y configuración' },
      { value: '4', label: 'Proveedor', description: 'Acceso a proveedores' },
      { value: '5', label: 'Cliente', description: 'Acceso básico de cliente' }
    ];
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <Users className="w-6 h-6 mr-3" style={{color: '#4DD5AF'}} />
                Crear Nuevo Usuario
              </h1>
            </div>
          </div>
        </div>

        {/* Mensaje de estado */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
            message.type === 'danger' ? 'bg-red-50 text-red-800 border border-red-200' :
            message.type === 'warning' ? 'bg-yellow-50 text-yellow-800 border border-yellow-200' :
            'bg-blue-50 text-blue-800 border border-blue-200'
          }`}>
            {message.type === 'success' ? <Check className="w-5 h-5 mr-2" /> : 
             <AlertCircle className="w-5 h-5 mr-2" />}
            {message.text}
          </div>
        )}

        {/* Formulario */}
        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Nombre Completo */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-2" />
                  Nombre Completo*
                </label>
                <input
                  type="text"
                  value={formData.nombrec}
                  onChange={(e) => handleInputChange('nombrec', e.target.value)}
                  placeholder="Nombre completo del usuario"
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all ${
                    errors.nombrec ? 'border-red-500' : 'border-gray-300'
                  }`}
                  onFocus={(e) => !errors.nombrec && (e.target.style.borderColor = '#4DD5AF')}
                  onBlur={(e) => !errors.nombrec && (e.target.style.borderColor = '#e5e7eb')}
                />
                {errors.nombrec && <p className="text-red-500 text-xs mt-1">{errors.nombrec}</p>}
              </div>

              {/* Nombre de Usuario */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-2" />
                  Nombre de Usuario*
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  placeholder="Nombre de usuario único"
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all ${
                    errors.username ? 'border-red-500' : 'border-gray-300'
                  }`}
                  onFocus={(e) => !errors.username && (e.target.style.borderColor = '#4DD5AF')}
                  onBlur={(e) => !errors.username && (e.target.style.borderColor = '#e5e7eb')}
                />
                {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email*
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="correo@ejemplo.com"
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  onFocus={(e) => !errors.email && (e.target.style.borderColor = '#4DD5AF')}
                  onBlur={(e) => !errors.email && (e.target.style.borderColor = '#e5e7eb')}
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              {/* Contraseña */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  <Lock className="w-4 h-4 inline mr-2" />
                  Contraseña*
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Contraseña segura"
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  onFocus={(e) => !errors.password && (e.target.style.borderColor = '#4DD5AF')}
                  onBlur={(e) => !errors.password && (e.target.style.borderColor = '#e5e7eb')}
                />
                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
              </div>

              {/* Tipo de Usuario */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  <UserCog className="w-4 h-4 inline mr-2" />
                  Tipo de Usuario
                </label>
                <select
                  value={formData.permisos}
                  onChange={(e) => handleInputChange('permisos', e.target.value)}
                  className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all bg-white"
                  style={{borderColor: '#e5e7eb'}}
                  onFocus={(e) => e.target.style.borderColor = '#4DD5AF'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                >
                  {getUserTypeLabels().map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label} - {type.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* Clínica Matriz Principal */}
              <div className="relative">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  <Building className="w-4 h-4 inline mr-2" />
                  Clínica Matriz Principal
                </label>
                <input
                  type="text"
                  value={formData.clinica_matriz}
                  onChange={(e) => handleInputChange('clinica_matriz', e.target.value)}
                  placeholder="Buscar clínica principal..."
                  className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all bg-white"
                  style={{borderColor: '#e5e7eb'}}
                  onFocus={(e) => e.target.style.borderColor = '#4DD5AF'}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    setTimeout(() => setShowClientSuggestions(false), 200);
                  }}
                />
                
                {/* Sugerencias de clientes principales */}
                {showClientSuggestions && filteredClients.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border-2 rounded-lg shadow-lg max-h-48 overflow-y-auto" style={{borderColor: '#4DD5AF'}}>
                    {filteredClients.slice(0, 5).map(client => (
                      <div
                        key={client.id}
                        onClick={() => {
                          handleInputChange('clinica_matriz', client.razon_social);
                          setShowClientSuggestions(false);
                        }}
                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{client.razon_social}</div>
                        {client.cif && <div className="text-sm text-gray-500">CIF: {client.cif}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Límite de gasto de este usuario */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  <CreditCard className="w-4 h-4 inline mr-2" />
                  Límite de gasto por pedido (opcional)
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Desde (€)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.importe_min}
                      onChange={(e) => handleInputChange('importe_min', e.target.value)}
                      placeholder="0.00"
                      className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all bg-white"
                      style={{borderColor: '#e5e7eb'}}
                      onFocus={(e) => e.target.style.borderColor = '#4DD5AF'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">Hasta (€)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.importe_max}
                      onChange={(e) => handleInputChange('importe_max', e.target.value)}
                      placeholder="∞ sin techo"
                      className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all bg-white"
                      style={{borderColor: '#e5e7eb'}}
                      onFocus={(e) => e.target.style.borderColor = '#4DD5AF'}
                      onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Techo de importe que <strong>este usuario</strong> puede pedir sin aprobación (dentro de su clínica principal).
                  Si un pedido lo supera, <strong>no se bloquea</strong>: pasa a «Pendientes de aprobación». Dejá «Hasta» vacío para «sin techo».
                  <br />
                  Tiene <strong>prioridad</strong> sobre el límite general del cliente. Requiere una clínica principal asignada.
                </p>
              </div>

              {/* Aprobador de pedidos */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  <UserCog className="w-4 h-4 inline mr-2" />
                  Aprobador de pedidos (opcional)
                </label>
                <select
                  value={formData.aprobador_id}
                  onChange={(e) => handleInputChange('aprobador_id', e.target.value)}
                  className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all bg-white"
                  style={{ borderColor: '#e5e7eb' }}
                  onFocus={(e) => (e.target.style.borderColor = '#4DD5AF')}
                  onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                >
                  <option value="">— Sin aprobador (lo aprueba la clínica matriz) —</option>
                  {aprobadores.map((a) => (
                    <option key={a.id} value={a.id}>
                      {(a.nombrec || a.username)}{a.permisos === 1 ? ' (admin)' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-2">
                  Quién aprueba los pedidos de este usuario cuando pasan a «Pendientes de aprobación».
                  Si dejás «Sin aprobador», siguen el flujo de siempre (los aprueba la clínica matriz).
                  El desplegable muestra usuarios de la red de la clínica más los administradores.
                </p>
              </div>

              {/* Proveedor Asociado */}
              <div className="relative">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  <Handshake className="w-4 h-4 inline mr-2" />
                  Proveedor Asociado
                </label>
                <input
                  type="text"
                  value={formData.proveedor_asociado}
                  onChange={(e) => handleInputChange('proveedor_asociado', e.target.value)}
                  placeholder="Buscar proveedor..."
                  className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all bg-white"
                  style={{borderColor: '#e5e7eb'}}
                  onFocus={(e) => e.target.style.borderColor = '#4DD5AF'}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    setTimeout(() => setShowProviderSuggestions(false), 200);
                  }}
                />
                
                {/* Sugerencias de proveedores */}
                {showProviderSuggestions && filteredProviders.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border-2 rounded-lg shadow-lg max-h-48 overflow-y-auto" style={{borderColor: '#4DD5AF'}}>
                    {filteredProviders.slice(0, 5).map(provider => (
                      <div
                        key={provider.id}
                        onClick={() => {
                          handleInputChange('proveedor_asociado', provider.nombre);
                          setShowProviderSuggestions(false);
                        }}
                        className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{provider.nombre}</div>
                        {provider.telefono && <div className="text-sm text-gray-500">Tel: {provider.telefono}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sección de Clínicas Matriz Adicionales */}
            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <Building className="w-5 h-5 mr-2" style={{color: '#4DD5AF'}} />
                Clínicas Matriz Adicionales
                <span className="text-sm font-normal text-gray-500 ml-2">(Opcional)</span>
              </h3>
              
              {/* Campo para agregar nueva clínica */}
              <div className="flex gap-2 mb-4">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={nuevaClinicaAdicional}
                    onChange={(e) => handleAdditionalClientChange(e.target.value)}
                    placeholder="Buscar clínica adicional..."
                    className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all bg-white"
                    style={{borderColor: '#e5e7eb'}}
                    onFocus={(e) => e.target.style.borderColor = '#4DD5AF'}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      setTimeout(() => setShowAdditionalClientSuggestions(false), 200);
                    }}
                  />
                  
                  {/* Sugerencias de clientes adicionales */}
                  {showAdditionalClientSuggestions && filteredAdditionalClients.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border-2 rounded-lg shadow-lg max-h-48 overflow-y-auto" style={{borderColor: '#4DD5AF'}}>
                      {filteredAdditionalClients.slice(0, 5).map(client => (
                        <div
                          key={client.id}
                          onClick={() => {
                            setNuevaClinicaAdicional(client.razon_social);
                            setShowAdditionalClientSuggestions(false);
                          }}
                          className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{client.razon_social}</div>
                          {client.cif && <div className="text-sm text-gray-500">CIF: {client.cif}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={agregarClinicaAdicional}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all flex items-center"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar
                </button>
              </div>

              {/* Lista de clínicas adicionales */}
              {clinicasAdicionales.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">Clínicas asignadas:</h4>
                  {clinicasAdicionales.map((cliente) => (
                    <div
                      key={cliente.id}
                      className="flex items-center justify-between bg-white p-3 rounded-lg border"
                    >
                      <div>
                        <div className="font-medium text-gray-900">{cliente.razon_social}</div>
                        {cliente.cif && <div className="text-sm text-gray-500">CIF: {cliente.cif}</div>}
                      </div>
                      <button
                        type="button"
                        onClick={() => eliminarClinicaAdicional(cliente.id)}
                        className="p-1 text-red-500 hover:text-red-700 transition-colors"
                        title="Eliminar clínica"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {clinicasAdicionales.length === 0 && (
                <p className="text-sm text-gray-500 italic">No se han agregado clínicas adicionales</p>
              )}
            </div>

            {/* Botones */}
            <div className="flex justify-center gap-4 mt-8">
              {/* Botón Volver */}
              <button
                onClick={handleGoBack}
                className="px-6 py-3 bg-gray-500 text-white rounded-lg font-medium transition-all hover:bg-gray-600 flex items-center"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver al listado de usuarios
              </button>

              {/* Botón Crear */}
              <button
                onClick={handleSubmit}
                disabled={saving}
                className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center text-white ${
                  saving ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                }`}
                style={{backgroundColor: '#4DD5AF'}}
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Creando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Crear Usuario
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}